import React, { useCallback, useState, useEffect } from "react";
import ReactDataGrid from "react-data-grid";
import { useField } from "react-final-form";
import { findIndex } from "lodash";
import { useNotify } from "react-admin";
import { isNumber } from "utils/tools";

interface IAreaDatagrid {
  edit?: boolean;
  areas: ISubcontractorAreas[];
  isDisabled?: boolean;
  type: "regular" | "cca";
  itemsToSendField: string;
}

interface IItem {
  subcontractorAreaId: number;
  area: string;
  feeVehicleSizeSmall: number;
  feeVehicleSizeMedium: number;
  feeVehicleSizeLarge: number;
  type: string;
}

interface IRateChartInsert {
  subcontractorAreaId: number;
  vehicleSize: string;
  fee: number;
  rateChartItemType: string;
}

const PriceFormatter = ({ value }: any) => {
  return (
    <span>
      {Number(Math.round((Number(value) + Number.EPSILON) * 100) / 100)}
    </span>
  );
};

const columns = [
  { key: "area", name: "Area" },
  {
    key: "feeVehicleSizeSmall",
    name: "Vehicle Size Small",
    editable: true,
    formatter: PriceFormatter,
  },
  {
    key: "feeVehicleSizeMedium",
    name: "Vehicle Size Medium",
    editable: true,
    formatter: PriceFormatter,
  },
  {
    key: "feeVehicleSizeLarge",
    name: "Vehicle Size Large",
    editable: true,
    formatter: PriceFormatter,
  },
];

const SubcontractorRateChartAreaDatagrid = ({
  areas,
  edit,
  isDisabled,
  type,
  itemsToSendField
}: IAreaDatagrid) => {
  const [items, setItems] = useState<IItem[] | []>([]);
  const [isLoaded, setLoaded] = useState(false);

  const notify = useNotify();

  const subcontractorRateChartItems = useField("items");
  const itemsToSend = useField(itemsToSendField);

  //populate items data with updated areas data from parent component
  useEffect(() => {
    let rowData: IItem[] = [];

    if (!edit) {
      //prefill empty placeholder for create-case
      areas.forEach(({ order, id, maxDistanceInMeters }: any) => {
        const data: IItem = {
          area: `Area ${order} (${maxDistanceInMeters}m)`,
          subcontractorAreaId: id,
          feeVehicleSizeLarge: 0,
          feeVehicleSizeMedium: 0,
          feeVehicleSizeSmall: 0,
          type
        };
        rowData.push(data);
      });
    }
    setItems(rowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas]);

  useEffect(() => {
    if (areas.length !== 0) {
      if (!isLoaded && subcontractorRateChartItems.input.value !== "") {
        let rowData: IItem[] = [];

        areas.forEach(({ order, id, maxDistanceInMeters }: any) => {
          const data: IItem = {
            area: `Area ${order} (${maxDistanceInMeters}m)`,
            subcontractorAreaId: id,
            feeVehicleSizeLarge: 0,
            feeVehicleSizeMedium: 0,
            feeVehicleSizeSmall: 0,
            type
          };
          rowData.push(data);
        });

        const rateChartItems = subcontractorRateChartItems.input.value.filter((item: any) => item.rateChartItemType === type);
        rateChartItems.forEach((item: any) => {
          let area = findIndex(rowData, {
            subcontractorAreaId: item.subcontractorAreaId,
          });

          if (item.vehicleSize === "small") {
            rowData[area].feeVehicleSizeSmall = item.fee / 100;
          }

          if (item.vehicleSize === "medium") {
            rowData[area].feeVehicleSizeMedium = item.fee / 100;
          }

          if (item.vehicleSize === "large") {
            rowData[area].feeVehicleSizeLarge = item.fee / 100;
          }

          rowData[area].type = item.rateChartItemType;
        });

        setItems(rowData);
        setLoaded(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcontractorRateChartItems.input.value, areas]);

  //update react-form data provider if there is a change in items
  useEffect(() => {
    const insertData: IRateChartInsert[] = [];

    items.forEach((item: IItem) => {
      insertData.push({
        subcontractorAreaId: item.subcontractorAreaId,
        vehicleSize: "small",
        fee: Number(item.feeVehicleSizeSmall) * 100,
        rateChartItemType: item.type,
      });
      insertData.push({
        subcontractorAreaId: item.subcontractorAreaId,
        vehicleSize: "medium",
        fee: Number(item.feeVehicleSizeMedium) * 100,
        rateChartItemType: item.type,
      });
      insertData.push({
        subcontractorAreaId: item.subcontractorAreaId,
        vehicleSize: "large",
        fee: Number(item.feeVehicleSizeLarge) * 100,
        rateChartItemType: item.type,
      });
    });

    itemsToSend.input.onChange(insertData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const onGridRowsUpdated = useCallback(
    ({
      fromRow,
      toRow,
      updated,
    }: ReactDataGrid.GridRowsUpdatedEvent<IItem>) => {
      const updatedRows = items.slice();

      if (
        (updated.hasOwnProperty("feeVehicleSizeSmall") &&
          !isNumber(Number(updated.feeVehicleSizeSmall))) ||
        (updated.hasOwnProperty("feeVehicleSizeMedium") &&
          !isNumber(Number(updated.feeVehicleSizeMedium))) ||
        (updated.hasOwnProperty("feeVehicleSizeLarge") &&
          !isNumber(Number(updated.feeVehicleSizeLarge)))
      ) {
        return notify(`Please input Number`, "warning");
      }

      for (let i = fromRow; i <= toRow; i++) {
        updatedRows[i] = { ...updatedRows[i], ...updated };
      }

      setItems(updatedRows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setItems, items]
  );

  if (areas.length === 0) {
    return <h4>Subcontractor Area data is not set.</h4>;
  }

  if (items.length === 0) {
    return <h4>Loading...</h4>;
  } else {
    return (
      <ReactDataGrid
        columns={columns}
        rowGetter={(i) => items[i]}
        rowsCount={items.length}
        enableCellSelect={isDisabled ? false : true}
        onGridRowsUpdated={onGridRowsUpdated}
        minWidth={700}
      />
    );
  }
};
export default SubcontractorRateChartAreaDatagrid;
